import { Flex, Spin, notification } from 'antd';
import { TextComponent } from 'baseComponents/TextComponent';
import { AdminErrorPage } from 'components/AdminErrorPage';
import { AdminPageWrapper } from 'components/layouts/AdminPageWrapper';
import { queryClient } from 'constants/query-client';
import { useEditUser } from 'hooks/useEditUser';
import { useGetUserDetail } from 'hooks/useGetUserDetail';
import { Navigate, useNavigate, useParams } from 'react-router';
import { AdminUserForm, UserFormValues } from 'components/AdminUserForm';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { useSetUserStatus } from 'hooks/useSetUserStatus';
import { useResetUserPassword } from 'hooks/useResetUserPassword';
import { useEditUserStores } from 'hooks/useEditUserStores';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';

export const AdminEditUser = () => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const params = useParams();

  const {
    data: userData,
    isLoading,
    isRefetching,
    isError,
    refetch,
    error,
  } = useGetUserDetail(params.id);

  const { mutate: editUser, isPending } = useEditUser({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['users'],
      });
      refetch();
      return api.success({
        message: `User has been edited.`,
        description: `User has been successfully edited.`,
        duration: 3,
      });
    },
    onError: () => {
      return api.error({
        message: 'Something went wrong',
        description: `Something went wrong with editing user. Please try again later.`,
        duration: 3,
      });
    },
  });

  const { mutate: editUserStores, isPending: isPendingUserStores } =
    useEditUserStores({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['users'],
        });
        refetch();
        return api.success({
          message: `User has been edited.`,
          description: `User stores have been successfully edited.`,
          duration: 3,
        });
      },
      onError: () => {
        return api.error({
          message: 'Something went wrong',
          description: `Something went wrong with assigning stores to the user. Please try again later.`,
          duration: 3,
        });
      },
    });

  const { mutate: resetPassword, isPending: resetPasswordPending } =
    useResetUserPassword({
      onSuccess: () => {
        return api.success({
          message: 'Pasword reset success',
          description: `Reset link has been sent.`,
          duration: 3,
        });
      },
      onError: () => {
        return api.error({
          message: 'Something went wrong',
          description: `Something went wrong with sending reset password link. Please try again later.`,
          duration: 3,
        });
      },
    });

  const { mutate: setUserStatus, isPending: isSetUserStatusPending } =
    useSetUserStatus({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['users'],
        });
        refetch();
        return api.success({
          message: `User has been ${
            userData?.is_active ? 'deactivated' : 'activated'
          }`,
          description: `User has been successfully ${
            userData?.is_active ? 'deactivated' : 'activated'
          }`,
          duration: 3,
        });
      },
      onError: () => {
        return api.error({
          message: 'Something went wrong',
          description: `Something went wrong with ${
            userData?.is_active ? 'deactivating' : 'activating'
          } user. Please try again later.`,
          duration: 3,
        });
      },
    });

  const handleActivateUser = () => {
    setUserStatus({ id: params.id || '', isActive: !userData?.is_active });
  };

  if (!params.id) return <Navigate to='/admin/manage/users' />;

  if (isError) {
    return (
      <AdminErrorPage
        isRefetching={isRefetching}
        refetch={refetch}
        error={error.response?.data?.message || 'Error fetching user details'}
      />
    );
  }

  const handleEditUser = (data: UserFormValues) => {
    editUser({
      id: params.id!,
      data: {
        name: data.name,
        email: data.email,
        phone_number: data.phone,
      },
    });
    if (data.stores && data.stores.length !== 0) {
      editUserStores({
        user_id: parseInt(params.id!),
        store_ids: data.stores || undefined,
      });
    } else {
      return api.error({
        message: 'Something went wrong',
        description: 'User must have at least one store assigned.',
        duration: 3,
      });
    }
  };
  return (
    <AdminPageWrapper
      sidebar={
        <Flex gap={8} vertical style={{ maxWidth: 320, marginTop: 64 }}>
          <ButtonComponent
            label='Reset password'
            appereance='primary'
            size='middle'
            fullWidth
            onClick={() => {
              resetPassword(userData?.email!);
            }}
          />

          <ButtonComponent
            label={userData?.is_active ? 'Deactivate' : 'Activate'}
            onClick={handleActivateUser}
            appereance='secondary'
            size='middle'
            fullWidth
          />
        </Flex>
      }
    >
      {contextHolder}
      <TextComponent size='table-title-size' text='User details' />
      {/* another loader for time of loading user details to avoid form initialation with data = undefined */}
      {isLoading ? (
        <Flex justify='center' align='center' style={{ height: 200 }}>
          <Spin size='large' />
        </Flex>
      ) : (
        <AdminUserForm
          onCancel={() => navigate('/admin/manage/users')}
          // undefined is required to display placeholders correctly
          defaultValues={{
            name: userData?.name ?? '',
            email: userData?.email ?? '',
            phone: userData?.phone_number ?? '',
            role: userData?.role_id ?? undefined,
            stores: userData?.stores?.map(({ id }) => id) ?? [],
            franchiseOwner: userData?.franchise_owner_id ?? undefined,
          }}
          submitButtonLabel='Save changes'
          mode='edit'
          onSubmit={(data) => handleEditUser(data)}
        />
      )}
      <FullScreenLoaderComponent
        spinning={
          isPendingUserStores ||
          isPending ||
          resetPasswordPending ||
          isSetUserStatusPending
        }
      />
    </AdminPageWrapper>
  );
};
